import { useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useDiagnostic } from "@/features/diagnostic/DiagnosticContext";
import { postIntake, EMAIL_RE, PHONE_RE } from "@/lib/intakeApi";
import { Container, PrimaryButton } from "@/components/site/Primitives";
import { DiagnosticHeader, SubmittedContext } from "@/components/diagnostic/Chrome";
import { IntakeClosed, useIntakeOpen } from "@/components/forms/IntakeClosed";
import { Check, CountrySelect, Field, FormAlert, Honeypot, describe, inputClass } from "@/components/forms/Fields";

const ORG_TYPES = [
  ["smb", "SMB"], ["clinic", "Clinic / Practice"], ["law_firm", "Law Firm"],
  ["nonprofit", "Nonprofit"], ["government", "Government"], ["other", "Other"],
];

function validate(c) {
  const e = {};
  if (c.full_name.trim().length < 2) e.full_name = "Enter your full name.";
  if (!EMAIL_RE.test(c.email.trim())) e.email = "Enter a valid email address.";
  if (!c.organization.trim()) e.organization = "Enter your organization name.";
  if (!c.country) e.country = "Select your country.";
  if (c.callback_requested && !PHONE_RE.test(c.phone.trim())) e.phone = "Enter a phone number with country code, e.g. +1 555 123 4567.";
  if (!c.consent) e.consent = "Please agree so we can contact you about this assessment.";
  return e;
}

function payload(state) {
  const c = state.contact;
  const opt = (v) => (v.trim() ? v.trim() : null);
  return {
    full_name: c.full_name.trim(), email: c.email.trim(), organization: c.organization.trim(), country: c.country,
    organization_type: c.organization_type || null, callback_requested: c.callback_requested,
    phone: c.callback_requested ? c.phone.trim() : null,
    callback_time: c.callback_requested ? opt(c.callback_time) : null,
    callback_timezone: c.callback_requested ? opt(c.callback_timezone) : null,
    need_text: state.submitted.details, need_ids: state.submitted.needs, consent: c.consent,
    idempotency_key: state.lead.key, website: c.website || null,
  };
}

export default function ContactStep() {
  const { state, setContact, leadSaved } = useDiagnostic();
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState("");
  const [pending, setPending] = useState(false);
  const justSaved = useRef(false);
  const open = useIntakeOpen();

  if (!state.submitted) return <Navigate to="/" replace />;
  if (state.lead?.id) return <Navigate to={justSaved.current ? "/diagnostic" : "/"} replace />;
  const c = state.contact;
  const set = (k) => (v) => { setContact({ [k]: v }); setErrors((e) => ({ ...e, [k]: undefined })); };
  const text = (k) => ({ value: c[k], onChange: (e) => set(k)(e.target.value) });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (pending) return;
    const v = validate(c);
    setErrors(v);
    if (Object.keys(v).length) return setAlert("Please complete the highlighted fields.");
    setPending(true);
    setAlert("");
    const res = await postIntake("/leads", payload(state));
    setPending(false);
    if (!res.ok) {
      setErrors(res.fieldErrors);
      return setAlert(res.message);
    }
    justSaved.current = true;
    leadSaved(res.data.id, res.data.answer_token);
  };

  return (
    <div data-testid="contact-step-page" className="theme-dark relative min-h-screen bg-canvas text-fg">
      <DiagnosticHeader onExit={() => navigate("/")} />
      <Container className="pb-24 pt-10">
        <div className="grid gap-12 lg:grid-cols-[300px_1fr] lg:gap-20">
          <SubmittedContext submitted={state.submitted} onEdit={() => navigate("/")} />
          {open !== true ? <IntakeClosed loading={open === null} /> : (
          <form onSubmit={onSubmit} noValidate className="relative min-w-0 max-w-2xl">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-tone">Before we continue</p>
            <h1 className="mt-4 font-display text-3xl font-light tracking-[-0.03em] sm:text-4xl">Where should our team follow up?</h1>
            <p className="mt-4 text-[15px] leading-relaxed text-subtle">
              When you continue, we save these contact details together with the business need you described, so our
              team can review your assessment and contact you about it.
            </p>
            <div className="mt-10 grid gap-6 sm:grid-cols-2">
              <Field id="full_name" label="Full name" required error={errors.full_name}>
                <input {...describe("full_name", errors.full_name)} {...text("full_name")} autoComplete="name" maxLength={120} data-testid="lead-full-name" className={inputClass} />
              </Field>
              <Field id="email" label="Work email" required error={errors.email} hint="A personal email is fine if you run your own business.">
                <input {...describe("email", errors.email, true)} {...text("email")} type="email" autoComplete="email" maxLength={254} data-testid="lead-email" className={inputClass} />
              </Field>
              <Field id="organization" label="Organization name" required error={errors.organization}>
                <input {...describe("organization", errors.organization)} {...text("organization")} autoComplete="organization" maxLength={160} data-testid="lead-organization" className={inputClass} />
              </Field>
              <Field id="country" label="Country" required error={errors.country}>
                <CountrySelect id="country" value={c.country} onChange={set("country")} error={errors.country} testId="lead-country" />
              </Field>
              <Field id="organization_type" label="Organization type">
                <select {...describe("organization_type")} {...text("organization_type")} data-testid="lead-org-type" className={inputClass}>
                  <option value="">Select a type</option>
                  {ORG_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>
            </div>
            <div className="mt-8 space-y-6">
              <Check id="callback" checked={c.callback_requested} onChange={set("callback_requested")} testId="lead-callback">I’d like a callback</Check>
              {c.callback_requested && (
                <div className="grid gap-6 border-l border-fg/15 pl-5 sm:grid-cols-2">
                  <Field id="phone" label="Phone with country code" required error={errors.phone}>
                    <input {...describe("phone", errors.phone)} {...text("phone")} type="tel" autoComplete="tel" placeholder="+1 555 123 4567" maxLength={32} data-testid="lead-phone" className={inputClass} />
                  </Field>
                  <Field id="callback_time" label="Preferred callback time">
                    <input {...describe("callback_time")} {...text("callback_time")} placeholder="e.g. Weekdays, 9–11am" maxLength={120} data-testid="lead-callback-time" className={inputClass} />
                  </Field>
                  <Field id="callback_timezone" label="Time zone">
                    <input {...describe("callback_timezone")} {...text("callback_timezone")} placeholder="e.g. GMT+1, Eastern Time" maxLength={64} data-testid="lead-callback-timezone" className={inputClass} />
                  </Field>
                </div>
              )}
              <Check id="consent" checked={c.consent} onChange={set("consent")} error={errors.consent} testId="lead-consent">
                I agree that FreelancAI may contact me about this assessment.
              </Check>
              <p className="text-[12px] leading-relaxed text-subtle">
                We use these details only to review your request and contact you about it. Please don’t include medical, legal or other
                confidential information. See our <Link to="/privacy" data-testid="lead-privacy-link" className="fa-link rounded-sm text-fg underline underline-offset-4">privacy notice</Link>.
              </p>
            </div>
            <Honeypot value={c.website} onChange={set("website")} />
            <div className="mt-8 space-y-4">
              <FormAlert message={alert} testId="lead-form-alert" />
              <PrimaryButton type="submit" disabled={pending} aria-busy={pending} data-testid="lead-continue-button">
                {pending ? "Saving…" : "Continue my assessment"} {!pending && <ArrowRight size={16} aria-hidden />}
              </PrimaryButton>
            </div>
          </form>
          )}
        </div>
      </Container>
    </div>
  );
}
